import AnimateHeight from "@/app/(site)/_components/Atoms/AnimateHeight"
import IconCheck from "@/app/(site)/_components/Atoms/Icons/IconCheck"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonText from "@/app/(site)/_components/Molecules/ButtonText"
import { SortModel } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { convertToSnakeCase } from "@/lib/utils"
import { t } from "@/translations"
import { FC } from "react"
import { sortConfig } from "../../types/options"

type SortProps = {
    setSort: (sort: string) => void
    selectedSort: SortModel | undefined
    langCode: string
    setToggleSort: (toggleSort: boolean) => void
    toggleSort: boolean
    clearSort: () => void
}

export const Sort: FC<SortProps> = ({
    selectedSort,
    setSort,
    langCode,
    setToggleSort,
    toggleSort,
    clearSort
}) => {
    const defaultSort: SortModel = sortConfig._relevance // Default sort option

    return (
        <div className="relative right-0 flex">
            <ButtonText
                asChild
                size="big"
                weight="regular"
                onClick={() => setToggleSort(!toggleSort)}
                role="button"
                aria-label={`Sorted by ${selectedSort?.meta?.title ?? defaultSort.meta?.title ?? ""}`}
                className="ml-auto flex"
            >
                <span>
                    {selectedSort?.meta?.title
                        ? t(
                              langCode,
                              `category_page.${convertToSnakeCase(selectedSort.meta.title)}`
                          )
                        : t(
                              langCode,
                              `category_page.${convertToSnakeCase(defaultSort.meta?.title || "")}`
                          )}
                </span>
                <IconChevronDown
                    className={`ml-8 h-24 w-24 origin-center transform ${
                        toggleSort ? "animate-flipUp" : "animate-flipDown"
                    }`}
                />
            </ButtonText>
            <AnimateHeight
                minified={!toggleSort}
                className="absolute right-0 top-26 w-auto translate-x-[24px] bg-white transition-all duration-300 ease-in-out"
            >
                <div className="flex flex-col gap-16 p-24">
                    {Object.entries(sortConfig).map(([key, { field, meta }]) => {
                        const isChecked =
                            (selectedSort?.field === field &&
                                selectedSort?.order === meta?.values?.[0]) ||
                            (!selectedSort && key === "_relevance")
                        return (
                            <div
                                key={`${field}-${meta?.title}`}
                                className="relative flex"
                                role="radiogroup"
                                aria-labelledby="sortOptions"
                            >
                                <input
                                    id={`${field}-${meta?.title}`}
                                    type="radio"
                                    className="absolute left-0 top-0 h-24 w-24 cursor-pointer rounded-full opacity-0"
                                    onChange={() => {
                                        if (key === "_relevance") {
                                            // Clear sorting from URL and reset to default
                                            clearSort()
                                        } else {
                                            // Set sorting in URL
                                            setSort(key)
                                        }
                                        setToggleSort(false)
                                    }}
                                    checked={isChecked}
                                    aria-label={`Select ${meta?.title}`}
                                    aria-checked={isChecked}
                                />
                                <div
                                    className={`mr-8 flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full border ${
                                        isChecked ? "bg-black" : "border-main-black"
                                    }`}
                                    aria-checked={isChecked}
                                >
                                    {isChecked ? (
                                        <IconCheck className="h-16 w-16 text-main-white" />
                                    ) : null}
                                </div>
                                <label
                                    htmlFor={`${field}-${meta?.title}`}
                                    className="cursor-pointer select-none"
                                >
                                    <Text size="medium" weight="regular">
                                        {t(
                                            langCode,
                                            `category_page.${convertToSnakeCase(meta?.title || "")}`
                                        )}
                                    </Text>
                                </label>
                            </div>
                        )
                    })}
                </div>
            </AnimateHeight>
        </div>
    )
}
